/**
 * FlowChatWidget — Session API–powered chat widget
 *
 * This is the NEW chat widget that uses the FlowEngine via the Session API.
 * Deterministic routing, server-side flow evaluation, no AI in decisions.
 *
 * Usage:
 *   <FlowChatWidget chatbotId="abc-123" />
 *   <FlowChatWidget campaignSlug="my-campaign" />
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, Minimize2, Bot, Loader2 } from 'lucide-react';
import type { FlowNode, FlowSession, RouteDestination, FlowDefinition } from '@/engine/types';
import { FlowEngine } from '@/engine/FlowEngine';
import { supabase } from '@/integrations/supabase/client';

// ─── Types ────────────────────────────────────────────────

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface FlowChatWidgetProps {
  chatbotId?: string;
  campaignId?: string;
  campaignSlug?: string;
  initialOpen?: boolean;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  onClose?: () => void;
  embedded?: boolean; // If true, renders without float button
}

// ─── API Helpers ──────────────────────────────────────────

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL || 'https://khwvlnkrtrwvfbnnjsrc.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imtod3ZsbmtydHJ3dmZibm5qc3JjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU2OTg3NzcsImV4cCI6MjA5MTI3NDc3N30.dwuWnP6n-mZQZjewpwjXZugvxIJmxKONzhIUV51Eu2g';

async function apiCall(path: string, body?: Record<string, unknown>, method: 'GET' | 'POST' = 'POST') {
  const url = `${SUPABASE_URL}/functions/v1/${path}`;
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json();
}

// ─── Main Component ────────────────────────────────────────

export function FlowChatWidget({
  chatbotId,
  campaignId,
  campaignSlug,
  initialOpen = false,
  position = 'bottom-right',
  onClose,
  embedded = false,
}: FlowChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(initialOpen || embedded);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentNode, setCurrentNode] = useState<FlowNode | null>(null);
  const [session, setSession] = useState<FlowSession | null>(null);
  const [ended, setEnded] = useState(false);
  const [destination, setDestination] = useState<RouteDestination | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  // Client-side engine: used when Edge Functions aren't available.
  // The FlowEngine is pure TypeScript so it runs identically in browser or Deno.
  const engineRef = useRef<FlowEngine | null>(null);
  const [clientSideMode, setClientSideMode] = useState(false);

  // Post-submit offer hub: when a session ends, we fetch the user's
  // OTHER published chatbots so we can present them as "you might also
  // qualify for" offers. This is the continuous-monetization layer —
  // turning a single click into multiple revenue paths even AFTER the
  // first route fires.
  interface AlternativeOffer {
    id: string;
    name: string;
    description: string | null;
  }
  const [alternativeOffers, setAlternativeOffers] = useState<AlternativeOffer[]>([]);

  // Scroll messages container only (never the page)
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isTyping]);

  // Initialize session when widget first opens
  useEffect(() => {
    if (!isOpen || initialized.current || !chatbotId) return;
    initialized.current = true;
    startSession();
  }, [isOpen, chatbotId]);

  // Post-submit offer hub: when a session ends, fetch the user's other
  // published chatbots and show them as "you might also qualify for"
  // cards. We don't await this — if it fails, the end-state UI just
  // shows the standard "all done" message.
  //
  // Why fetch this CLIENT-SIDE? Two reasons:
  //   1. Privacy: the widget runs on a third-party site, so we don't
  //      want to leak the chatbot owner's full chatbot list to the
  //      session-message response payload by default.
  //   2. Performance: only fetched once per session, only after route,
  //      and only when the user is in the "engaged but considering"
  //      state — never blocks the conversation hot path.
  useEffect(() => {
    if (!ended || !chatbotId) return;
    let cancelled = false;
    (async () => {
      try {
        // Find the owner of the current chatbot, then their other published bots.
        const { data: cb } = await supabase
          .from('chatbots')
          .select('user_id')
          .eq('id', chatbotId)
          .maybeSingle();
        if (!cb?.user_id || cancelled) return;
        const { data: others } = await supabase
          .from('chatbots')
          .select('id, name, description')
          .eq('user_id', cb.user_id)
          .eq('published', true)
          .neq('id', chatbotId)
          .limit(3);
        if (!cancelled && others) setAlternativeOffers(others as AlternativeOffer[]);
      } catch {
        // Silent failure — alternative offers are nice-to-have, never block the end state
      }
    })();
    return () => { cancelled = true; };
  }, [ended, chatbotId]);

  const addBotMessage = useCallback((text: string) => {
    setMessages(prev => [...prev, {
      id: `bot-${Date.now()}-${Math.random()}`,
      text,
      sender: 'bot',
      timestamp: new Date(),
    }]);
  }, []);

  const addUserMessage = useCallback((text: string) => {
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}-${Math.random()}`,
      text,
      sender: 'user',
      timestamp: new Date(),
    }]);
  }, []);

  /**
   * Load flow nodes directly from Supabase and build an in-browser FlowEngine.
   * This enables the widget to work without Edge Functions deployed.
   */
  const buildClientEngine = async (): Promise<FlowEngine | null> => {
    if (!chatbotId) return null;
    try {
      const [nodesRes, defRes] = await Promise.all([
        supabase.from('flow_nodes').select('*').eq('chatbot_id', chatbotId).order('position'),
        supabase.from('flow_definitions').select('*').eq('chatbot_id', chatbotId).maybeSingle(),
      ]);

      const rows = (nodesRes.data || []) as Array<FlowNode & { chatbot_id: string }>;
      if (rows.length === 0) return null;

      // Build the nodes map keyed by id
      const nodesMap: Record<string, FlowNode> = {};
      for (const r of rows) {
        nodesMap[r.id] = {
          id: r.id,
          type: r.type,
          field: r.field,
          question: r.question,
          input_type: (r as any).input_type,
          options: r.options || [],
          validation: r.validation,
          rules: r.rules || [],
          next: r.next || [],
          meta: r.meta || {},
        };
      }

      const startNode = defRes.data?.start_node || rows[0].id;
      const flow: FlowDefinition = {
        id: defRes.data?.id || `flow-${chatbotId}`,
        name: defRes.data?.name || 'Flow',
        start_node: startNode,
        nodes: nodesMap,
      };

      return new FlowEngine(flow);
    } catch (err) {
      console.warn('Failed to build client-side engine:', err);
      return null;
    }
  };

  /**
   * Pull intent context from the browser — page URL, referrer, UTM params.
   * These are the "where did this user come from" signals that drive the
   * router's decisions. Called once at session start.
   *
   * Everything is opt-in — if the widget is running inside an iframe and
   * `window.location` is denied, or UTMs aren't present, we just return
   * nulls. Nothing blocks on missing context.
   */
  const captureIntentContext = (): Record<string, string | null> => {
    try {
      // If embedded via iframe into a parent, `window.top !== window`. We
      // still read our OWN location and referrer — the parent doc's URL is
      // inaccessible due to same-origin policy, so this is the best we can do.
      const url = new URL(window.location.href);
      const params = url.searchParams;
      return {
        page_url: window.location.href,
        referrer: document.referrer || null,
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        utm_term: params.get('utm_term'),
        utm_content: params.get('utm_content'),
      };
    } catch {
      // Sandboxed iframe / blocked window access — silently return empty context
      return {
        page_url: null, referrer: null,
        utm_source: null, utm_medium: null, utm_campaign: null,
        utm_term: null, utm_content: null,
      };
    }
  };

  const startSession = async () => {
    if (!chatbotId) {
      setError('Missing chatbot ID');
      return;
    }
    setIsTyping(true);

    // Capture intent context ONCE at session start. All seven fields are
    // optional on the server side (session-start nullish-coalesces them).
    const intentContext = captureIntentContext();

    // Try server-side first (Edge Function). Fall back to client-side if unavailable.
    try {
      const result = await apiCall('session-start', {
        chatbot_id: chatbotId,
        campaign_id: campaignId,
        ...intentContext,
      });
      setSession(result.session || { id: result.session_id, current_node_id: result.node?.id, answers: {}, history: [] });
      setCurrentNode(result.node);
      setEnded(result.ended || false);
      if (result.node?.question) {
        setTimeout(() => {
          setIsTyping(false);
          addBotMessage(result.node.question);
        }, 600);
      } else {
        setIsTyping(false);
      }
      return;
    } catch (err: any) {
      // Server unavailable → switch to client-side mode
      console.info('Session API unavailable, using client-side engine:', err.message);
    }

    // ── Client-side fallback ──
    const engine = await buildClientEngine();
    if (!engine) {
      setIsTyping(false);
      addBotMessage("This chatbot isn't ready yet. Please add some flow nodes in the builder.");
      setEnded(true);
      return;
    }
    engineRef.current = engine;
    setClientSideMode(true);

    const sessionId = `local-${Date.now()}`;
    const result = engine.startSession(sessionId);
    setSession(result.session);
    setCurrentNode(result.node);
    setEnded(result.ended || false);
    if (result.node?.question) {
      setTimeout(() => {
        setIsTyping(false);
        addBotMessage(result.node!.question!);
      }, 600);
    } else {
      setIsTyping(false);
    }
  };

  const sendAnswer = async (answer: string) => {
    if (!session || ended) return;
    addUserMessage(answer);
    setIsTyping(true);

    // ── Client-side mode ── run the engine locally
    if (clientSideMode && engineRef.current) {
      try {
        const result = engineRef.current.processAnswer(session, answer);
        if (result.session) setSession(result.session);
        if (result.node) setCurrentNode(result.node);
        if (result.ended) setEnded(true);
        if (result.destination) {
          setDestination(result.destination);
          if (result.destination.type === 'redirect' && result.destination.url) {
            setTimeout(() => { window.location.href = result.destination!.url!; }, 2000);
          }
        }
        if (result.node?.question) {
          setTimeout(() => { setIsTyping(false); addBotMessage(result.node!.question!); }, 700);
        } else {
          setIsTyping(false);
        }
      } catch (err: any) {
        setIsTyping(false);
        addBotMessage(err.message || "Please check your answer and try again.");
      }
      return;
    }

    // ── Server-side mode ── call Edge Function
    try {
      const result = await apiCall('session-message', {
        session_id: session.id,
        answer,
      });

      // Handle validation failure (422)
      if (result.validation_error) {
        setTimeout(() => {
          setIsTyping(false);
          addBotMessage(result.validation_error || "Please check your answer and try again.");
        }, 600);
        return;
      }

      if (result.session) setSession(result.session);
      if (result.node) setCurrentNode(result.node);
      if (result.ended) setEnded(true);
      if (result.destination) {
        setDestination(result.destination);
        // If redirect type, do the redirect after a brief pause
        if (result.destination.type === 'redirect' && result.destination.url) {
          setTimeout(() => {
            window.location.href = result.destination.url;
          }, 2000);
        }
      }

      if (result.node?.question) {
        setTimeout(() => {
          setIsTyping(false);
          addBotMessage(result.node.question);
        }, 800);
      } else {
        setIsTyping(false);
      }
    } catch (err: any) {
      setIsTyping(false);
      addBotMessage("Something went wrong. Let's try again.");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text || isTyping || ended) return;
    setInputValue('');
    sendAnswer(text);
  };

  const handleOptionClick = (option: string) => {
    if (isTyping || ended) return;
    sendAnswer(option);
  };

  // Position classes for floating widget
  const positionClasses: Record<string, string> = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
  };

  const WidgetContent = (
    <div className={`flex flex-col ${embedded ? 'w-full h-full' : 'w-[380px] h-[560px]'} rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl overflow-hidden`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <Bot className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">IntentDrive</div>
          <div className="text-xs text-emerald-100/80 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
            Online
          </div>
        </div>
        {!embedded && (
          <button onClick={() => { setIsOpen(false); onClose?.(); }} className="text-white/70 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-900">
        {messages.map((msg) => (
          <motion.div
            key={msg.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
              msg.sender === 'user'
                ? 'bg-emerald-500 text-white rounded-br-md'
                : 'bg-slate-800 text-slate-200 rounded-bl-md'
            }`}>
              {msg.text}
            </div>
          </motion.div>
        ))}

        {/* Quick-reply buttons from current node options */}
        {!isTyping && !ended && currentNode?.options && currentNode.options.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {currentNode.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => handleOptionClick(opt)}
                className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-xs text-slate-200 hover:bg-emerald-500/20 hover:border-emerald-500/40 hover:text-emerald-300 transition-all"
              >
                {opt}
              </button>
            ))}
          </div>
        )}

        {isTyping && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
            <div className="bg-slate-800 px-4 py-2.5 rounded-2xl rounded-bl-md flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </motion.div>
        )}

        {ended && destination?.type === 'redirect' && (
          <div className="text-center text-xs text-slate-500 py-2">Redirecting you now...</div>
        )}
        {ended && destination?.type !== 'redirect' && (
          <div className="text-center text-xs text-emerald-400 py-2">✓ All done! Thanks for chatting.</div>
        )}

        {/* Post-submit offer hub: continuous monetization layer */}
        {ended && alternativeOffers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
            className="pt-3 mt-2 border-t border-slate-800 space-y-2"
          >
            <div className="text-[11px] text-slate-500 uppercase tracking-wider px-1">
              You might also qualify for
            </div>
            {alternativeOffers.map((offer) => (
              <button
                key={offer.id}
                onClick={() => {
                  // Navigate the parent window to the alternative offer's
                  // standalone page. window.top works for top-level pages
                  // and falls back gracefully when sandboxed iframes block it.
                  const url = `https://intentdrive.ai/c/${offer.id}`;
                  try {
                    if (window.top) window.top.location.href = url;
                    else window.location.href = url;
                  } catch {
                    window.location.href = url;
                  }
                }}
                className="w-full text-left p-2.5 rounded-lg bg-slate-800/60 hover:bg-emerald-500/10 border border-slate-700 hover:border-emerald-500/40 transition-all group"
              >
                <div className="text-xs font-semibold text-slate-200 group-hover:text-emerald-300">
                  {offer.name}
                </div>
                {offer.description && (
                  <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">
                    {offer.description}
                  </div>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </div>

      {/* Input */}
      {!ended && (
        <form onSubmit={handleSubmit} className="border-t border-slate-800 p-3 flex items-center gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={isTyping ? 'Please wait...' : 'Type your answer...'}
            disabled={isTyping}
            className="flex-1 bg-slate-800/60 border border-slate-700 rounded-full px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={isTyping || !inputValue.trim()}
            className="w-9 h-9 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isTyping ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Send className="h-4 w-4 text-white" />}
          </button>
        </form>
      )}
    </div>
  );

  // Embedded mode: render directly
  if (embedded) return WidgetContent;

  // Floating mode: render with toggle button
  return (
    <div className={`fixed ${positionClasses[position]} z-50`}>
      <AnimatePresence>
        {isOpen ? (
          <motion.div
            key="widget"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {WidgetContent}
          </motion.div>
        ) : (
          <motion.button
            key="button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setIsOpen(true)}
            className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 shadow-xl shadow-emerald-500/30 flex items-center justify-center hover:scale-105 transition-transform"
          >
            <Bot className="h-6 w-6 text-white" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export default FlowChatWidget;
